import React, { useEffect, useReducer, useState } from "react";

/**
 * ClockFlyout — Windows 11-style flyout opened by clicking the tray clock.
 * Shows a navigable month calendar plus the 5 world clocks (EDT/CDT/MDT/PDT/IST).
 *
 * The world-clock zone math mirrors components/WorldClock.jsx (kept local to
 * avoid importing the header-styled component). If the zone list there changes,
 * mirror it here.
 */

const ZONES = [
  { tz: "America/New_York", std: "EST", dst: "EDT", color: "#1f6feb" },
  { tz: "America/Chicago", std: "CST", dst: "CDT", color: "#b7791f" },
  { tz: "America/Denver", std: "MST", dst: "MDT", color: "#c05621" },
  { tz: "America/Los_Angeles", std: "PST", dst: "PDT", color: "#2f855a" },
  { tz: "Asia/Kolkata", std: "IST", dst: "IST", color: "#9d4edd" },
];

const getUTCOffset = (date, tz) => {
  const utc = new Date(date.toLocaleString("en-US", { timeZone: "UTC" }));
  const local = new Date(date.toLocaleString("en-US", { timeZone: tz }));
  return (local - utc) / 60000;
};
const isInDST = (tz) => {
  const now = new Date();
  const jan = new Date(now.getFullYear(), 0, 1);
  const jul = new Date(now.getFullYear(), 6, 1);
  const stdOffset = Math.min(getUTCOffset(jan, tz), getUTCOffset(jul, tz));
  return getUTCOffset(now, tz) !== stdOffset;
};
const timeInZone = (tz) =>
  new Date().toLocaleTimeString("en-US", { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: true });

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Build the day-cell grid for a given year/month (leading blanks + day numbers).
const buildGrid = (year, month) => {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
  return cells;
};

const ClockFlyout = ({ onClose }) => {
  const today = new Date();
  const [view, setView] = useState({ year: today.getFullYear(), month: today.getMonth() });

  // Tick once a second so the world clocks stay live.
  const [, tick] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const prevMonth = () =>
    setView((v) => (v.month === 0 ? { year: v.year - 1, month: 11 } : { ...v, month: v.month - 1 }));
  const nextMonth = () =>
    setView((v) => (v.month === 11 ? { year: v.year + 1, month: 0 } : { ...v, month: v.month + 1 }));

  const isToday = (d) =>
    d === today.getDate() && view.month === today.getMonth() && view.year === today.getFullYear();

  const cells = buildGrid(view.year, view.month);

  return (
    <div className="webtop-flyout webtop-clock-flyout" role="dialog" aria-label="Calendar and world clocks">
      <div className="webtop-cal">
        <div className="webtop-cal__head">
          <span className="webtop-cal__title">{MONTHS[view.month]} {view.year}</span>
          <div className="webtop-cal__nav">
            <button type="button" aria-label="Previous month" onClick={prevMonth}>‹</button>
            <button
              type="button"
              aria-label="Today"
              onClick={() => setView({ year: today.getFullYear(), month: today.getMonth() })}
            >
              •
            </button>
            <button type="button" aria-label="Next month" onClick={nextMonth}>›</button>
          </div>
        </div>
        <div className="webtop-cal__grid">
          {WEEKDAYS.map((w) => (
            <span key={w} className="webtop-cal__wd">{w}</span>
          ))}
          {cells.map((d, i) => (
            <span
              key={i}
              className={`webtop-cal__day${d && isToday(d) ? " webtop-cal__day--today" : ""}${d ? "" : " webtop-cal__day--empty"}`}
            >
              {d || ""}
            </span>
          ))}
        </div>
      </div>

      <div className="webtop-worldclocks">
        <div className="webtop-worldclocks__title">World clocks</div>
        {ZONES.map((z) => (
          <div key={z.tz} className="webtop-worldclock">
            <span className="webtop-worldclock__zone" style={{ color: z.color }}>
              {isInDST(z.tz) ? z.dst : z.std}
            </span>
            <span className="webtop-worldclock__time">{timeInZone(z.tz)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export { ZONES, buildGrid };
export default ClockFlyout;
